import _ from 'lodash';

export interface IParsedGatewayLogLine {
  levelLabel: string;
  /** Normalized numeric level when present (e.g. Hypergate JSON `level`). */
  levelNumber: number | null;
  summary: string;
  project?: string;
  functionName?: string;
  jobType?: string;
  loggerName?: string;
}

function safeJsonParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return undefined;
  }
}

function truncateSummary(s: string, max = 320): string {
  return _.truncate(s, { length: max, omission: '…' });
}

function normalizeLevelNumber(level: unknown): number | null {
  if (level === undefined || level === null) {
    return null;
  }
  if (_.isNumber(level) && _.isFinite(level)) {
    return level;
  }
  if (_.isString(level)) {
    const trimmed = level.trim();
    const n = parseInt(trimmed, 10);
    if (!_.isNaN(n) && String(n) === trimmed) {
      return n;
    }
  }
  return null;
}

function baseFromRoot(r: Record<string, unknown>): Pick<IParsedGatewayLogLine, 'levelLabel' | 'levelNumber' | 'project'> {
  const lvl = r.level !== undefined ? String(r.level) : '';
  return {
    levelLabel: lvl,
    levelNumber: normalizeLevelNumber(r.level),
    project: _.isString(r.project) ? r.project : undefined,
  };
}

function mergeParsed(
  r: Record<string, unknown>,
  extra: Partial<IParsedGatewayLogLine> & { summary: string },
): IParsedGatewayLogLine {
  const b = baseFromRoot(r);
  return {
    ...b,
    summary: extra.summary,
    levelLabel: extra.levelLabel ?? b.levelLabel,
    levelNumber: extra.levelNumber ?? b.levelNumber,
    project: extra.project ?? b.project,
    functionName: extra.functionName,
    jobType: extra.jobType,
    loggerName: extra.loggerName,
  };
}

function shortPreviewValue(v: unknown): string {
  if (v === null || v === undefined) {
    return String(v);
  }
  if (_.isArray(v)) {
    return '';
  }
  if (_.isPlainObject(v)) {
    return '';
  }
  if (_.isString(v)) {
    return _.truncate(v, { length: 48, omission: '…' });
  }
  return String(v);
}

/** e.g. `{"code":3000,"message":"unknown session ID"}` → readable one-liner for synthesis. */
function formatHypergateErrorObject(o: Record<string, unknown>): string {
  const parts: string[] = [];
  if (o.code !== undefined) {
    parts.push(`code ${String(o.code)}`);
  }
  if (_.isString(o.message) && o.message.trim()) {
    parts.push(_.truncate(o.message.trim(), { length: 160, omission: '…' }));
  } else if (o.message !== undefined && !_.isPlainObject(o.message)) {
    parts.push(_.truncate(String(o.message), { length: 120, omission: '…' }));
  }
  if (parts.length > 0) {
    return parts.join(' · ');
  }
  return '';
}

/** e.g. `{"userId":"C0ADE09F"}` → `C0ADE09F` for synthesis (id only, no `userId` label). */
function formatHypergateUserObject(o: Record<string, unknown>): string {
  if (_.isString(o.userId) && o.userId.trim()) {
    return o.userId.trim();
  }
  if (o.userId !== undefined && !_.isPlainObject(o.userId)) {
    return String(o.userId);
  }
  return '';
}

function shortPreviewValueForField(fieldKey: string, v: unknown): string {
  if (fieldKey === 'error' && _.isPlainObject(v)) {
    return formatHypergateErrorObject(v as Record<string, unknown>);
  }
  if (fieldKey === 'user' && _.isPlainObject(v)) {
    return formatHypergateUserObject(v as Record<string, unknown>);
  }
  return shortPreviewValue(v);
}

/** Omits `: …` when the value has no short preview (nested object/array). */
function formatDataFieldSegment(fieldKey: string, value: unknown): string {
  const pv = shortPreviewValueForField(fieldKey, value).trim();
  return pv ? `${fieldKey}: ${pv}` : fieldKey;
}

function formatMacHexForDisplay(s: string): string {
  const h = s.replace(/[^a-f0-9]/gi, '');
  if (h.length !== 12) {
    return s;
  }
  return h.match(/.{2}/g)!.join(':').toUpperCase();
}

/**
 * Collects lock-related bits from a rule object (no MAC from credential map keys — those are often gateway/device).
 */
function collectLockHintsFromRule(rule: Record<string, unknown>): string[] {
  const parts: string[] = [];
  if (_.isString(rule.macAddress) && rule.macAddress.trim()) {
    const h = rule.macAddress.replace(/[^a-f0-9]/gi, '');
    if (h.length === 12) {
      parts.push(`MAC ${formatMacHexForDisplay(h)}`);
    }
  }
  if (rule.lockId !== undefined) {
    parts.push(`id ${String(rule.lockId)}`);
  }
  if (rule.backendLockId !== undefined) {
    parts.push(`lock id ${String(rule.backendLockId)}`);
  }
  if (_.isString(rule.lockName) && rule.lockName.trim()) {
    parts.push(`"${_.truncate(rule.lockName.trim(), { length: 48, omission: '…' })}"`);
  } else if (_.isString(rule.name) && rule.name.trim() && !_.includes(rule.name, '{')) {
    parts.push(`"${_.truncate(rule.name.trim(), { length: 48, omission: '…' })}"`);
  }
  if (rule.deviceId !== undefined) {
    const did = String(rule.deviceId);
    if (did.length <= 64) {
      parts.push(`device ${did}`);
    }
  }
  if (_.isPlainObject(rule.smartLock)) {
    const sl = rule.smartLock as Record<string, unknown>;
    if (_.isString(sl.name) && sl.name.trim()) {
      const label = `"${_.truncate(sl.name.trim(), { length: 48, omission: '…' })}"`;
      if (!parts.some((p) => p === label)) {
        parts.push(label);
      }
    }
    if (sl.id !== undefined) {
      parts.push(`smartLock id ${String(sl.id)}`);
    }
  }
  return _.uniq(parts);
}

function collectLockHintsFromPlainObject(o: Record<string, unknown>): string[] {
  let hints = collectLockHintsFromRule(o);
  if (_.isPlainObject(o.lock)) {
    hints = _.concat(hints, collectLockHintsFromRule(o.lock as Record<string, unknown>));
  }
  if (_.isPlainObject(o.smartLock)) {
    hints = _.concat(hints, collectLockHintsFromRule({ smartLock: o.smartLock } as Record<string, unknown>));
  }
  return _.uniq(_.compact(hints));
}

/** Scan nested objects (payload, job args, etc.) for lock fields — depth high enough for SEND_LOCK_LOGS payloads. */
function gatherLockHintsFromData(data: unknown, depth = 0): string[] {
  if (depth > 8 || data === undefined || data === null) {
    return [];
  }
  if (_.isString(data)) {
    const t = data.trim();
    if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
      const parsed = safeJsonParse(data);
      if (_.isPlainObject(parsed) || _.isArray(parsed)) {
        return gatherLockHintsFromData(parsed, depth + 1);
      }
    }
    return [];
  }
  if (_.isPlainObject(data)) {
    const o = data as Record<string, unknown>;
    let hints = collectLockHintsFromPlainObject(o);
    if (_.isPlainObject(o.credentials)) {
      const creds = o.credentials as Record<string, unknown>;
      const firstKey = _.keys(creds)[0];
      const firstArr = firstKey ? creds[firstKey] : undefined;
      if (_.isArray(firstArr) && firstArr.length > 0 && _.isPlainObject(firstArr[0])) {
        hints = _.concat(hints, collectLockHintsFromRule(firstArr[0] as Record<string, unknown>));
      }
    }
    _.forEach(['message', 'payload', 'details', 'body', 'data', 'functionArgs', 'job', 'args'], (k) => {
      if (o[k] !== undefined) {
        hints = _.concat(hints, gatherLockHintsFromData(o[k], depth + 1));
      }
    });
    return _.uniq(_.compact(hints));
  }
  if (_.isArray(data)) {
    return _.flatten(
      _.map(data.slice(0, 12), (item) => gatherLockHintsFromData(item, depth + 1)),
    );
  }
  return [];
}

function appendLockAppendixToSummary(summary: string, data: unknown): string {
  if (!_.isPlainObject(data)) {
    return summary;
  }
  if (_.includes(summary, 'Lock:')) {
    return summary;
  }
  const hints = gatherLockHintsFromData(data);
  if (hints.length === 0) {
    return summary;
  }
  const appendix = hints.join(', ');
  if (_.includes(summary, appendix)) {
    return summary;
  }
  return `${summary} · Lock: ${appendix}`;
}

/** doExecute → JOB: merge lock hints from job arg row + full nested message (payload with macAddress). */
function appendJobArrowSummaryWithLockHints(
  base: string,
  jobArgRow: Record<string, unknown>,
  nestedMessage: Record<string, unknown>,
): string {
  if (_.includes(base, 'Lock:')) {
    return base;
  }
  const hints = _.uniq(
    _.compact([
      ...gatherLockHintsFromData(jobArgRow),
      ...gatherLockHintsFromData(nestedMessage),
    ]),
  );
  if (hints.length === 0) {
    return base;
  }
  return `${base} · Lock: ${hints.join(', ')}`;
}

/**
 * Lock MAC only when the log line contains JSON `macAddress` (plain or escaped), e.g.
 * "macAddress\":\"00:15:42:04:8d:48\" or "macAddress":"00:15:42:04:8d:48"
 */
function extractLockMacFromMacAddressJsonPattern(line: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const pushColonMac = (macStr: string) => {
    const h = macStr.replace(/[^a-f0-9]/gi, '');
    if (h.length !== 12) {
      return;
    }
    const label = `MAC ${formatMacHexForDisplay(h)}`;
    if (!seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  };

  let m: RegExpExecArray | null;
  // Escaped JSON inside a string: macAddress\":\"00:15:42:04:8d:48\"
  const reEscaped = /macAddress\\"+:\\"+([0-9a-fA-F:]{17})\\"+/gi;
  while ((m = reEscaped.exec(line)) !== null) {
    pushColonMac(m[1]);
  }
  // Plain JSON fragment
  const rePlain = /"macAddress"\s*:\s*"([0-9a-fA-F:]{17})"/gi;
  while ((m = rePlain.exec(line)) !== null) {
    pushColonMac(m[1]);
  }
  // Compact 12 hex (no colons) after macAddress
  const rePlainCompact = /"macAddress"\s*:\s*"([0-9a-fA-F]{12})"/gi;
  while ((m = rePlainCompact.exec(line)) !== null) {
    pushColonMac(m[1]);
  }
  // Loose: macAddress then separators then value (Loki / double-escaped lines)
  const reLoose = /macAddress["'\\\s]*[:=]+["'\\\s]*([0-9a-fA-F]{2}(?::[0-9a-fA-F]{2}){5}|[0-9a-fA-F]{12})/gi;
  while ((m = reLoose.exec(line)) !== null) {
    pushColonMac(m[1]);
  }
  return out.slice(0, 4);
}

function parseRssiFieldValue(v: unknown): number | null {
  if (v === undefined || v === null) {
    return null;
  }
  if (_.isNumber(v) && _.isFinite(v)) {
    return v;
  }
  if (_.isString(v)) {
    const n = parseInt(v.trim(), 10);
    return _.isNaN(n) ? null : n;
  }
  return null;
}

function findFirstRssiDbmInData(data: unknown, depth = 0): number | null {
  if (depth > 12 || data === undefined || data === null) {
    return null;
  }
  if (_.isString(data)) {
    const fromEsc = extractRssiDbmFromLine(data);
    if (fromEsc !== null) {
      return fromEsc;
    }
    const trimmed = data.trim();
    if (trimmed.length > 0 && (trimmed.startsWith('{') || trimmed.includes('rssi'))) {
      const parsed = safeJsonParse(trimmed);
      if (parsed !== undefined && parsed !== data) {
        return findFirstRssiDbmInData(parsed, depth + 1);
      }
    }
    return null;
  }
  if (_.isPlainObject(data)) {
    const o = data as Record<string, unknown>;
    if (_.has(o, 'rssi')) {
      const n = parseRssiFieldValue(o.rssi);
      if (n !== null) {
        return n;
      }
    }
    for (const v of _.values(o)) {
      const r = findFirstRssiDbmInData(v, depth + 1);
      if (r !== null) {
        return r;
      }
    }
  }
  if (_.isArray(data)) {
    for (const item of data.slice(0, 32)) {
      const r = findFirstRssiDbmInData(item, depth + 1);
      if (r !== null) {
        return r;
      }
    }
  }
  return null;
}

/**
 * RSSI in raw Loki line — often escaped like `"rssi\\\":-78` (backslashes before the key quote).
 * `rssi(?:\\)*"` = rssi + zero or more `\` + `"` + `:` + number.
 */
function extractRssiDbmFromLine(line: string): number | null {
  const patterns = [
    /"rssi"\s*:\s*(-?\d+)/gi,
    /'rssi'\s*:\s*(-?\d+)/gi,
    /rssi(?:\\)*"\s*:\s*(-?\d+)/gi,
    /\\"rssi(?:\\)*"\s*:\s*(-?\d+)/gi,
    /rssi\\":\s*(-?\d+)/gi,
    /rssi\\":(-?\d+)/gi,
  ];
  for (const re of patterns) {
    re.lastIndex = 0;
    const m = re.exec(line);
    if (m) {
      const n = parseInt(m[1], 10);
      if (!_.isNaN(n)) {
        return n;
      }
    }
  }
  return null;
}

function enrichSummaryWithRssiFromRawLine(summary: string, rawLine: string): string {
  /** Do not skip when raw JSON in summary contains `"rssi"` — that blocked the formatted suffix. */
  if (/·\s*RSSI\s+-?\d+\s*dBm/i.test(summary)) {
    return summary;
  }
  const fromLine = extractRssiDbmFromLine(rawLine);
  if (fromLine !== null) {
    return `${summary} · RSSI ${fromLine} dBm`;
  }
  const root = safeJsonParse(rawLine);
  const fromObj = _.isPlainObject(root) ? findFirstRssiDbmInData(root) : null;
  if (fromObj !== null) {
    return `${summary} · RSSI ${fromObj} dBm`;
  }
  return summary;
}

function enrichSummaryWithMacFromRawLine(summary: string, rawLine: string): string {
  if (_.includes(summary, 'MAC ')) {
    return summary;
  }
  const macs = extractLockMacFromMacAddressJsonPattern(rawLine);
  if (macs.length === 0) {
    return summary;
  }
  const tail = macs.join(', ');
  if (_.includes(summary, 'Lock:')) {
    return `${summary}, ${tail}`;
  }
  return `${summary} · Lock: ${tail}`;
}

function finalizeSummaryWithMacEnrichment(summary: string, rawLine: string): string {
  const withMac = enrichSummaryWithMacFromRawLine(summary, rawLine);
  const withRssi = enrichSummaryWithRssiFromRawLine(withMac, rawLine);
  /** Reserve space for ` · RSSI -123 dBm` so truncation does not drop RSSI. */
  return truncateSummary(withRssi, 500);
}

function sealParsed(rawLine: string, parsed: IParsedGatewayLogLine): IParsedGatewayLogLine {
  return {
    ...parsed,
    summary: finalizeSummaryWithMacEnrichment(parsed.summary, rawLine),
  };
}

/** Human-readable summary for Hypergate `data` payloads (e.g. doExecute + credentials map). */
function summarizeHypergateData(functionName: string | undefined, data: unknown): string {
  if (data === undefined) {
    return '';
  }
  if (!_.isPlainObject(data)) {
    if (_.isString(data)) {
      return _.truncate(data, { length: 280, omission: '…' });
    }
    return truncateSummary(JSON.stringify(data), 200);
  }

  const d = data as Record<string, unknown>;
  const fn = functionName || '';

  if (_.isPlainObject(d.credentials)) {
    const creds = d.credentials as Record<string, unknown>;
    const deviceIds = _.keys(creds);
    let ruleCount = 0;
    _.forEach(creds, (val) => {
      if (_.isArray(val)) {
        ruleCount += val.length;
      }
    });
    const firstDevice = deviceIds[0];
    const firstArr = firstDevice ? creds[firstDevice] : undefined;
    let lockHints: string[] = [];
    if (_.isArray(firstArr) && firstArr.length > 0 && _.isPlainObject(firstArr[0])) {
      lockHints = collectLockHintsFromRule(firstArr[0] as Record<string, unknown>);
    }
    lockHints = _.uniq(lockHints);
    const base = `${deviceIds.length} device(s), ${ruleCount} rule(s)`;
    const lockSeg = lockHints.length > 0 ? ` · Lock: ${lockHints.join(', ')}` : '';
    const moreRules = ruleCount > 1 ? ' …' : '';
    if (!fn) {
      return `Credentials · ${base}${lockSeg}${moreRules}`;
    }
    return `${base}${lockSeg}${moreRules}`;
  }

  const keys = _.keys(d);
  if (keys.length === 0) {
    return appendLockAppendixToSummary('(empty object)', d);
  }
  if (keys.length <= 5) {
    return appendLockAppendixToSummary(
      keys.map((k) => formatDataFieldSegment(k, d[k])).join(' · '),
      d,
    );
  }
  const errFmt = _.isPlainObject(d.error)
    ? formatHypergateErrorObject(d.error as Record<string, unknown>).trim()
    : '';
  const errSeg = errFmt ? ` · error: ${errFmt}` : '';
  const userFmt = _.isPlainObject(d.user)
    ? formatHypergateUserObject(d.user as Record<string, unknown>).trim()
    : '';
  const userSeg = userFmt ? ` · user: ${userFmt}` : '';
  return appendLockAppendixToSummary(
    `${keys.slice(0, 4).join(', ')} … (+${keys.length - 4} fields)${errSeg}${userSeg}`,
    d,
  );
}

/**
 * Row background for gateway log level (subtle: level 4 = soft red, level 6 = white).
 */
export function getGatewayLogRowSurfaceStyle(levelNumber: number | null): {
  backgroundColor: string;
  borderLeft: string;
} {
  if (levelNumber === 4) {
    return {
      backgroundColor: 'rgba(183, 28, 28, 0.065)',
      borderLeft: '3px solid rgba(183, 28, 28, 0.32)',
    };
  }
  if (levelNumber === 6) {
    return {
      backgroundColor: '#ffffff',
      borderLeft: '3px solid rgba(0, 0, 0, 0.07)',
    };
  }
  return {
    backgroundColor: 'rgba(250, 250, 250, 0.98)',
    borderLeft: '3px solid rgba(0, 0, 0, 0.06)',
  };
}

/**
 * Parses Hypergate / GatewaySDK JSON log lines (often double-encoded) into summary, level, and optional structured fields.
 */
export function parseGatewayLogLine(line: string, depth = 0): IParsedGatewayLogLine {
  return sealParsed(line, parseGatewayLogLineInner(line, depth));
}

function parseGatewayLogLineInner(line: string, depth = 0): IParsedGatewayLogLine {
  const fallback: IParsedGatewayLogLine = {
    levelLabel: '',
    levelNumber: null,
    summary: truncateSummary(line),
  };

  if (depth > 4) {
    return fallback;
  }

  const root = safeJsonParse(line);
  if (!_.isPlainObject(root)) {
    return fallback;
  }

  const r = root as Record<string, unknown>;

  // Log4j-style: plain message (not Hypergate JSON-in-message)
  if (_.isString(r.message) && (r.loggerName !== undefined || r.instant !== undefined)) {
    return mergeParsed(r, {
      summary: truncateSummary(r.message, 400),
      loggerName: _.isString(r.loggerName) ? r.loggerName : undefined,
    });
  }

  const lvl = r.level !== undefined ? String(r.level) : '';
  let inner: unknown = r.message;

  if (_.isString(inner)) {
    const parsed = safeJsonParse(inner);
    inner = parsed !== undefined ? parsed : inner;
  }

  if (!_.isPlainObject(inner)) {
    const rawMsg = r.message;
    return mergeParsed(r, {
      summary: truncateSummary(_.isString(rawMsg) ? rawMsg : String(rawMsg)),
    });
  }

  const inn = inner as Record<string, unknown>;
  const nestedMsg = inn.message;

  if (_.isString(inn.functionName) && inn.data !== undefined) {
    const fn = inn.functionName;
    return mergeParsed(r, {
      summary: truncateSummary(`${fn}: ${summarizeHypergateData(fn, inn.data)}`),
      functionName: fn,
    });
  }

  if (_.isPlainObject(nestedMsg)) {
    const m = nestedMsg as Record<string, unknown>;
    const fn = _.isString(m.functionName) ? m.functionName : undefined;
    const jobArgs = m.functionArgs;

    if (_.isArray(jobArgs) && jobArgs.length > 0) {
      const first = jobArgs[0] as Record<string, unknown>;
      const job = _.isPlainObject(first.job) ? (first.job as Record<string, unknown>) : undefined;
      if (job && _.isString(job.type)) {
        const base = `${fn || 'execute'} → ${job.type}`;
        const withLock = appendJobArrowSummaryWithLockHints(base, first, m);
        return mergeParsed(r, {
          summary: truncateSummary(withLock, 420),
          functionName: fn,
          jobType: job.type,
        });
      }
    }

    if (fn !== undefined && m.data !== undefined) {
      const dataSummary = summarizeHypergateData(fn, m.data);
      return mergeParsed(r, {
        summary: truncateSummary(`${fn}: ${dataSummary}`),
        functionName: fn,
      });
    }

    if (fn !== undefined) {
      return mergeParsed(r, {
        summary: truncateSummary(fn),
        functionName: fn,
      });
    }

    if (_.isPlainObject(m.error)) {
      const err = m.error as Record<string, unknown>;
      if (err.code !== undefined || (_.isString(err.message) && err.message.trim())) {
        return mergeParsed(r, {
          summary: truncateSummary(`error: ${formatHypergateErrorObject(err)}`),
        });
      }
      const detail = err.detail !== undefined ? err.detail : err.entityIds;
      return mergeParsed(r, {
        summary: truncateSummary(`error: ${JSON.stringify(detail)}`),
      });
    }

    if (_.isPlainObject(m.data)) {
      const keys = _.keys(m.data as object).slice(0, 4);
      const raw = `${fn || 'data'}: ${keys.join(', ')} …`;
      return mergeParsed(r, {
        summary: truncateSummary(appendLockAppendixToSummary(raw, m.data)),
        functionName: fn,
      });
    }
  }

  if (_.isString(nestedMsg)) {
    const inner2 = safeJsonParse(nestedMsg);
    if (_.isPlainObject(inner2)) {
      const nested = parseGatewayLogLineInner(
        JSON.stringify({
          level: r.level,
          project: r.project,
          message: JSON.stringify(inner2),
        }),
        depth + 1,
      );
      return mergeParsed(r, {
        summary: nested.summary,
        levelLabel: nested.levelLabel || lvl,
        levelNumber: nested.levelNumber ?? normalizeLevelNumber(r.level),
        project: nested.project ?? (_.isString(r.project) ? r.project : undefined),
        functionName: nested.functionName,
        jobType: nested.jobType,
        loggerName: nested.loggerName,
      });
    }
  }

  const parts: string[] = [];
  if (_.isString(inn.deviceModel)) {
    parts.push(inn.deviceModel);
  }
  if (_.isString(inn.deviceUUID)) {
    parts.push(inn.deviceUUID);
  }
  if (parts.length > 0) {
    return mergeParsed(r, {
      summary: truncateSummary(parts.join(' · ')),
    });
  }

  return mergeParsed(r, {
    summary: truncateSummary(summarizeHypergateData(undefined, inn)),
  });
}
